/**
 * Sign-in helpers shared by every client: input validation for the email-code flow, reading the claims of the
 * 15-minute access token (no verification — the server verifies; clients only need sub/email/exp), and when to
 * refresh it.
 */

export type AccessTokenClaims = { userId: string; email?: string; expiresAt: number };

/** Refresh this long before the access token expires. */
export const REFRESH_LEAD_MS = 60_000;
/** After a failed (network) refresh, try again after this long. */
export const REFRESH_RETRY_MS = 30_000;
export const EMAIL_CODE_LENGTH = 6;

/** Loose syntactic check (the server is authoritative): one "@", a dot in the domain, no spaces, ≤ 254 chars. */
export function isValidEmail(input: string): boolean {
  const email = input.trim();
  if (email.length < 3 || email.length > 254) return false;
  const at = email.indexOf("@");
  if (at <= 0 || at !== email.lastIndexOf("@")) return false;
  const domain = email.slice(at + 1);
  const dot = domain.lastIndexOf(".");
  if (dot <= 0 || dot === domain.length - 1) return false;
  for (let i = 0; i < email.length; i++) {
    const c = email.charCodeAt(i);
    if (c <= 0x20 || c === 0x7f) return false;
  }
  return true;
}

/** Keeps digits only (users paste "123 456" or "123-456"), at most EMAIL_CODE_LENGTH. */
export function normalizeEmailCode(input: string): string {
  let out = "";
  for (let i = 0; i < input.length && out.length < EMAIL_CODE_LENGTH; i++) {
    const c = input.charCodeAt(i);
    if (c >= 0x30 && c <= 0x39) out += input[i];
  }
  return out;
}

export function isCompleteEmailCode(code: string): boolean {
  return code.length === EMAIL_CODE_LENGTH && normalizeEmailCode(code) === code;
}

const B64URL = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

function base64UrlBytes(input: string): number[] | undefined {
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i]!;
    if (ch === "=") break;
    const v = B64URL.indexOf(ch === "+" ? "-" : ch === "/" ? "_" : ch);
    if (v < 0) return undefined;
    buffer = (buffer << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  return bytes;
}

function utf8Decode(bytes: number[]): string | undefined {
  let out = "";
  for (let i = 0; i < bytes.length; ) {
    const b = bytes[i]!;
    let cp: number;
    let n: number;
    if (b < 0x80) [cp, n] = [b, 1];
    else if (b >= 0xc0 && b < 0xe0) [cp, n] = [b & 0x1f, 2];
    else if (b >= 0xe0 && b < 0xf0) [cp, n] = [b & 0x0f, 3];
    else if (b >= 0xf0 && b < 0xf8) [cp, n] = [b & 0x07, 4];
    else return undefined;
    if (i + n > bytes.length) return undefined;
    for (let k = 1; k < n; k++) {
      const c = bytes[i + k]!;
      if ((c & 0xc0) !== 0x80) return undefined;
      cp = (cp << 6) | (c & 0x3f);
    }
    out += String.fromCodePoint(cp);
    i += n;
  }
  return out;
}

/** sub / email / exp of a JWT access token (exp in epoch ms). Undefined when it isn't a readable JWT. */
export function accessTokenClaims(token: string): AccessTokenClaims | undefined {
  const parts = token.split(".");
  if (parts.length !== 3) return undefined;
  const bytes = base64UrlBytes(parts[1]!);
  const json = bytes === undefined ? undefined : utf8Decode(bytes);
  if (json === undefined) return undefined;
  let payload: unknown;
  try {
    payload = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (typeof payload !== "object" || payload === null) return undefined;
  const p = payload as { sub?: unknown; email?: unknown; exp?: unknown };
  if (typeof p.sub !== "string" || p.sub === "" || typeof p.exp !== "number") return undefined;
  const claims: AccessTokenClaims = { userId: p.sub, expiresAt: p.exp * 1000 };
  if (typeof p.email === "string" && p.email !== "") claims.email = p.email;
  return claims;
}

/** Milliseconds until the token should be refreshed (0 = now). */
export function refreshDelay(expiresAt: number, now: number): number {
  return Math.max(0, expiresAt - REFRESH_LEAD_MS - now);
}

/** The access token is unusable (or about to be) — refresh before sending a request with it. */
export function needsRefresh(expiresAt: number, now: number): boolean {
  return expiresAt - now < REFRESH_LEAD_MS / 2;
}
